import { Component, NgZone, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SocketIOService } from 'src/app/services/socket-io.service';
import { ClipViewQueueService } from 'src/app/services/clipview-queue.service';
import { PlaybookViewTimeQueueService } from 'src/app/services/playbook-view-time-queue.service';
import { Location } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from './services/local-storage.service';
import { isDataDeletionUrl } from './constants/data-deletion.constants';

declare const gtag: Function; // Google Analytics

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'QwikCut Access';
  publicDataDeletionPage: boolean;

  constructor(
    private zone: NgZone,
    private router: Router,
			private socketIOService: SocketIOService,
    private matIconReg: MatIconRegistry,
    private clipViewQueueService: ClipViewQueueService,
    private playbookViewTimeQueueService: PlaybookViewTimeQueueService,
    private location: Location,
    private localStorageService: LocalStorageService,
    private overlayContainer: OverlayContainer,
    private dialog: MatDialog,
  ) {
    // Direct links must choose the public shell before account navigation initializes.
    this.publicDataDeletionPage = isDataDeletionUrl(this.location.path());
    if (this.publicDataDeletionPage) this.setPublicPageTheme();

    this.router.onSameUrlNavigation = 'reload';
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

		/** START : Code to Track Page View using gtag.js */
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    )
		.subscribe({
			next: event => {
				const url = (<NavigationEnd>event).urlAfterRedirects;
        const wasPublicPage = this.publicDataDeletionPage;
        this.publicDataDeletionPage = isDataDeletionUrl(url);
        if (this.publicDataDeletionPage) {
          this.setPublicPageTheme();
          if (!wasPublicPage) this.dialog.closeAll();
        }
				gtag('event', 'page_view', {
					 page_path: url
				})
			 }
		});
		/** END : Code to Track Page View  using gtag.js */

    //this prevents duplicate keydown events from being propagated to the rest of the application
    // helps with performance, especially with video player keyboard shortcuts
    this.zone.runOutsideAngular(() => {
      let altKeyDown = false;
      window.document.addEventListener("keydown", (event: KeyboardEvent) => { 
        if (event.key === 'Alt') altKeyDown = true;
        if (event.repeat != undefined && event.repeat && (event.target == document.body || altKeyDown ||
          (event.target instanceof HTMLElement &&
            event.target.tagName.toUpperCase() != 'INPUT' &&
            event.target.tagName.toUpperCase() != 'TEXTAREA' && 
            !event.target.contentEditable))) {
              event.stopPropagation();
              event.stopImmediatePropagation();
              event.preventDefault();
        }
      });
      window.document.addEventListener("keyup", (event: KeyboardEvent) => { 
        if (event.key === 'Alt') altKeyDown = false;
				this.socketIOService.onAppInteraction();
      });
      window.document.addEventListener("mouseup", (event: MouseEvent) => {
				this.socketIOService.onAppInteraction();
      });
    })
  }

  ngOnInit(): void {
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
    if (!this.publicDataDeletionPage) {
      this.clipViewQueueService.flushCurrentUser();
      this.playbookViewTimeQueueService.flushCurrentUser();
    }
  }

  private setPublicPageTheme(): void {
    const theme = this.localStorageService.getTheme() === 'light' ? 'theme-light' : 'theme-dark';
    const previousTheme = theme === 'theme-light' ? 'theme-dark' : 'theme-light';
    document.body.classList.remove(previousTheme);
    document.body.classList.add(theme);
    const overlay = this.overlayContainer.getContainerElement();
    overlay.classList.remove(previousTheme);
    overlay.classList.add(theme);
  }
}
